import * as React from 'react';
import { useEffect, useState } from 'react';
import { SignupStep1 } from '../SignupStep1';
import { SignupStep2 } from '../SignupStep2';
import { SocialAuthTokenDataType } from './Signup.apollo';
import { Network, SocialAuthTokenLazyQueryHookResult } from '../../../models/graphql';
import { AuthContextEnum } from '../../../constants/auth';
import { IInvitation, ISocialOAuth } from '../../../models/auth';
import { useReactiveVar } from '@apollo/client';
import { InputTypes } from '../../../constants/form';
import { invitationDetailsVar, socialOAuthVar } from '../../../providers/AppProvider';

export type SignupControllerProps = {
  //
  initialStep: 1 | 2;
  submitted: boolean;
  signupError: string | JSX.Element;
  signupData?: SocialAuthTokenDataType;
  signupQuery: SocialAuthTokenLazyQueryHookResult[0];
};

export const SignupController = (props: SignupControllerProps): JSX.Element => {
  // Put All your methods and logic here
  const initialRegistrationEmail = props.signupData?.email || '';

  const [step, setStep] = useState<1 | 2>(props.initialStep);
  const [signupData, setSignupData] = useState<SocialAuthTokenDataType>(props.signupData);
  const [emailForRegistration, setEmailForRegistration] = useState<string>('');
  const socialOAuth: ISocialOAuth | null = useReactiveVar(socialOAuthVar);
  const invitation: IInvitation | null = useReactiveVar(invitationDetailsVar);

  // we pass down this function to signup step 2 component, where once user clicks on cancel with social signup,
  // it clears signupDataRef value and reverts to step 1. We need to clear FBsocialData using ref,
  // since Signup.apollo.tsx won't fire query again, and we need to make sure to clear data in this controller.
  // this is due to bug once user cancels social signup on step 2, go back to step 1 and enters email (for email signup)
  // it would have shown FacebookSignupLogin component with social signup data on step 2, if signupData had existed.
  const cancelSocialSignup = (): void => {
    setSignupData(undefined);
    setStep(1);
  };

  //w we check if initial email has been set, and if yes, trigger state update, in order to redirect to step 2,
  // we need to do this inside useEffect, since we pass down setEmailForRegistration to Step!, where it can be updated too
  useEffect(() => {
    if (initialRegistrationEmail) {
      setEmailForRegistration(initialRegistrationEmail);
    }
  }, [initialRegistrationEmail]);

  // triggers step update if initial step had been changed, we pass it down since it can be updated from the SignupStep 1 | 2 components
  useEffect(() => {
    setStep(props.initialStep);
  }, [props.initialStep]);

  useEffect(() => {
    if (props.signupData) {
      setSignupData(props.signupData);
    }
  }, [props.signupData]);

  // Check if we have reactive var socialOAuthVar set.
  // If yes, we want to fetch data from FB and once we receive data, set initialStep to 2
  // (in order to do the redirect in the controller)
  useEffect(() => {
    if (
      socialOAuth &&
      socialOAuth.context === AuthContextEnum.signup &&
      (socialOAuth.network === Network.FACEBOOK || socialOAuth.network === Network.GOOGLE)
    ) {
      props.signupQuery({ variables: { clientToken: socialOAuth.socialAuthToken } });
      socialOAuthVar(null);
    }
    // TODO-FIX-TS-WARNINGS
    // eslint-disable-next-line
  }, [socialOAuth]);

  return step === 2 && emailForRegistration ? (
    <SignupStep2
      cancelSocialSignup={cancelSocialSignup}
      signupData={signupData}
      submitted={props.submitted}
      email={emailForRegistration}
      passwordFiledType={signupData ? InputTypes.hidden : InputTypes.password}
    />
  ) : (
    <SignupStep1
      submitted={props.submitted}
      setStep={setStep}
      signupError={props.signupError}
      invitation={invitation}
      setEmailForRegistration={setEmailForRegistration}
    />
  );
};
